import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { ReptileCard } from "../components/ReptileCard";
import { Filter, X } from "lucide-react";
import { toast } from "sonner";

const TYPE_SPECIES = {
  snake: ["Ball Python", "Boa Constrictor", "Reticulated Python", "Carpet Python"],
  lizard: ["Leopard Gecko", "Crested Gecko", "Gargoyle Gecko"],
};

const Marketplace = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const typeParam = params.get("type");
  const [reptiles, setReptiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  const [mobileFilters, setMobileFilters] = useState(false);

  const [filters, setFilters] = useState({
    species: "",
    morph: "",
    sex: "any",
    min_price: "",
    max_price: "",
    sort: "newest",
  });

  const fetchReptiles = () => {
    setLoading(true);
    const params = {};
    if (filters.species) params.species = filters.species;
    if (filters.morph) params.morph = filters.morph;
    if (filters.sex !== "any") params.sex = filters.sex;
    if (filters.min_price) params.min_price = parseFloat(filters.min_price);
    if (filters.max_price) params.max_price = parseFloat(filters.max_price);
    params.sort = filters.sort;
    api.get("/reptiles", { params })
      .then((r) => {
        let data = r.data;
        // Client-side filter by type (snake/lizard)
        if (typeParam && TYPE_SPECIES[typeParam]) {
          const allowed = new Set(TYPE_SPECIES[typeParam]);
          data = data.filter((x) => allowed.has(x.species));
        } else if (typeParam === "rare") {
          data = data.filter((x) => x.featured);
        }
        setReptiles(data);
      })
      .catch(() => setReptiles([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReptiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, typeParam]);

  useEffect(() => {
    if (user) {
      api.get("/wishlist").then((r) => setWishlist(r.data.map((w) => w.item_id))).catch(() => {});
    } else {
      setWishlist([]);
    }
  }, [user]);

  const toggleWishlist = async (reptile) => {
    if (!user) { toast.error(t("auth.signIn")); return; }
    try {
      const r = await api.post("/wishlist/toggle", { item_id: reptile.id, item_type: "reptile" });
      setWishlist((prev) => r.data.added ? [...prev, reptile.id] : prev.filter((x) => x !== reptile.id));
      toast.success(r.data.added ? "♥" : "−");
    } catch (e) { toast.error("Error"); }
  };

  const species = useMemo(() => {
    const set = new Set(reptiles.map((r) => r.species));
    return Array.from(set);
  }, [reptiles]);

  const clearFilters = () => setFilters({ species: "", morph: "", sex: "any", min_price: "", max_price: "", sort: "newest" });

  const FiltersPanel = (
    <div className="space-y-7" data-testid="filters-panel">
      <div>
        <label className="overline block mb-3">{t("market.species")}</label>
        <select
          data-testid="filter-species"
          value={filters.species}
          onChange={(e) => setFilters({ ...filters, species: e.target.value })}
          className="w-full bg-black border border-white/10 px-3 py-2.5 text-sm text-white font-mono-tech focus:border-[var(--monzal-gold)] outline-none"
        >
          <option value="">{t("market.anyOption")}</option>
          {["Ball Python", "Boa Constrictor", "Leopard Gecko", "Crested Gecko", "Gargoyle Gecko", "Reticulated Python", "Carpet Python"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="overline block mb-3">{t("market.morph")}</label>
        <input
          data-testid="filter-morph"
          value={filters.morph}
          onChange={(e) => setFilters({ ...filters, morph: e.target.value })}
          placeholder="e.g. Pied"
          className="w-full bg-black border border-white/10 px-3 py-2.5 text-sm text-white font-mono-tech placeholder:text-gray-600 focus:border-[var(--monzal-gold)] outline-none"
        />
      </div>
      <div>
        <label className="overline block mb-3">{t("market.sex")}</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { v: "any", label: t("market.sexAny") },
            { v: "male", label: t("market.sexMale") },
            { v: "female", label: t("market.sexFemale") },
            { v: "unsexed", label: t("market.sexUnsexed") },
          ].map((opt) => (
            <button
              key={opt.v}
              data-testid={`filter-sex-${opt.v}`}
              onClick={() => setFilters({ ...filters, sex: opt.v })}
              className={`text-xs font-mono-tech uppercase py-2 border transition ${filters.sex === opt.v ? "border-[var(--monzal-gold)] text-[var(--monzal-gold)]" : "border-white/10 text-gray-400 hover:border-white/30"}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="overline block mb-3">{t("market.price")} (MXN)</label>
        <div className="grid grid-cols-2 gap-2">
          <input
            data-testid="filter-min-price"
            type="number"
            placeholder="Min"
            value={filters.min_price}
            onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
            className="bg-black border border-white/10 px-3 py-2.5 text-sm text-white font-mono-tech focus:border-[var(--monzal-gold)] outline-none"
          />
          <input
            data-testid="filter-max-price"
            type="number"
            placeholder="Max"
            value={filters.max_price}
            onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
            className="bg-black border border-white/10 px-3 py-2.5 text-sm text-white font-mono-tech focus:border-[var(--monzal-gold)] outline-none"
          />
        </div>
      </div>
      <div>
        <label className="overline block mb-3">{t("market.sort")}</label>
        <select
          data-testid="filter-sort"
          value={filters.sort}
          onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
          className="w-full bg-black border border-white/10 px-3 py-2.5 text-sm text-white font-mono-tech focus:border-[var(--monzal-gold)] outline-none"
        >
          <option value="newest">{t("market.sortNewest")}</option>
          <option value="price_asc">{t("market.sortPriceAsc")}</option>
          <option value="price_desc">{t("market.sortPriceDesc")}</option>
        </select>
      </div>
      <button data-testid="filter-clear" onClick={clearFilters} className="btn-ghost w-full justify-center">
        {t("market.clear")}
      </button>
    </div>
  );

  return (
    <div data-testid="marketplace-page" className="bg-[var(--monzal-bg)] min-h-screen">
      <div className="border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <div className="overline">MARKETPLACE</div>
          <h1 className="font-display font-light text-4xl lg:text-6xl text-white tracking-tight mt-4">{t("market.title")}</h1>
          <p className="text-gray-400 mt-4 max-w-xl">{t("market.sub")}</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Mobile filter trigger */}
        <div className="lg:hidden flex items-center justify-between">
          <button data-testid="mobile-filter-trigger" onClick={() => setMobileFilters(true)} className="btn-ghost">
            <Filter size={14} /> {t("market.filters")}
          </button>
          <div className="font-mono-tech text-xs text-gray-500">
            {reptiles.length} {t("market.results")}
          </div>
        </div>

        {/* Desktop sidebar */}
        <aside className="hidden lg:block lg:col-span-3 sticky top-24 h-fit">
          <div className="overline mb-6">{t("market.filters")}</div>
          {FiltersPanel}
        </aside>

        {/* Mobile drawer */}
        {mobileFilters && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/80" onClick={() => setMobileFilters(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-[var(--monzal-surface)] border-l border-white/10 p-6 overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="overline">{t("market.filters")}</div>
                <button data-testid="mobile-filter-close" onClick={() => setMobileFilters(false)} className="text-white p-2">
                  <X size={18} />
                </button>
              </div>
              {FiltersPanel}
            </div>
          </div>
        )}

        <div className="lg:col-span-9">
          <div className="hidden lg:flex items-center justify-between mb-8">
            <div className="font-mono-tech text-xs text-gray-500">
              {reptiles.length} {t("market.results")}
            </div>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card-luxury aspect-[4/5] shimmer" />
              ))}
            </div>
          ) : reptiles.length === 0 ? (
            <div className="text-gray-500 text-center py-32 font-display tracking-wider" data-testid="marketplace-empty">
              {t("market.noResults")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reptiles.map((r, i) => (
                <div key={r.id} className="fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                  <ReptileCard reptile={r} onWishlist={toggleWishlist} isWishlisted={wishlist.includes(r.id)} testIdPrefix="market" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Marketplace;
